import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  CreateConversationResponse,
  SendMessageRequest,
  SendMessageResponse,
  GetConversationResponse,
  GetConversationsResponse,
} from '../types';

/**
 * Client for communicating with the AI Assistant backend API
 */
export class AiAssistantClient {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  /**
   * Create a new conversation
   */
  async createConversation(): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ai-assistant');
    
    const response = await this.fetchApi.fetch(`${baseUrl}/conversations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create conversation: ${response.status} ${errorText}`);
    }

    const data: CreateConversationResponse = await response.json();
    return data.conversationId;
  }

  /**
   * Send a message to a conversation
   */
  async sendMessage(
    conversationId: string,
    message: string,
    context?: string,
  ): Promise<SendMessageResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ai-assistant');
    
    const request: SendMessageRequest = { message, context: context as any };
    
    const response = await this.fetchApi.fetch(
      `${baseUrl}/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to send message: ${response.status} ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Send a message with streaming status updates.
   * Calls onStatus for each intermediate status, then returns the final response.
   */
  async sendMessageStreaming(
    conversationId: string,
    message: string,
    context?: string,
    onStatus?: (status: string) => void,
  ): Promise<SendMessageResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ai-assistant');
    
    const request: SendMessageRequest = { message, context: context as any };
    
    const response = await this.fetchApi.fetch(
      `${baseUrl}/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to send message: ${response.status} ${errorText}`);
    }

    // Try to read as a stream for real-time status updates
    if (response.body && typeof response.body.getReader === 'function') {
      return this.parseSSEStream(response.body, onStatus);
    }

    // Fallback: read full text and parse events (when streaming isn't supported)
    const text = await response.text();
    return this.parseSSEText(text, onStatus);
  }

  /**
   * Parse SSE from a ReadableStream (real-time, chunk by chunk)
   */
  private async parseSSEStream(
    body: ReadableStream<Uint8Array>,
    onStatus?: (status: string) => void,
  ): Promise<SendMessageResponse> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalResponse: SendMessageResponse | null = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete events in the buffer
      const events = buffer.split('\n\n');
      // Keep the last incomplete chunk in the buffer
      buffer = events.pop() || '';

      for (const eventBlock of events) {
        if (!eventBlock.trim()) continue;

        let eventType = '';
        let eventData = '';

        for (const line of eventBlock.split('\n')) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7);
          } else if (line.startsWith('data: ')) {
            eventData = line.slice(6);
          }
        }

        if (!eventType || !eventData) continue;

        try {
          const parsed = JSON.parse(eventData);
          if (eventType === 'status' && onStatus) {
            onStatus(parsed.status);
          } else if (eventType === 'done') {
            finalResponse = parsed;
          } else if (eventType === 'error') {
            throw new Error(parsed.message || 'Unknown error');
          }
        } catch (e) {
          if (e instanceof Error && (e.message !== 'Unknown error' && !e.message.includes('Failed'))) {
            // JSON parse error — skip
          } else {
            throw e;
          }
        }
      }
    }

    if (!finalResponse) {
      throw new Error('No response received from server');
    }

    return finalResponse;
  }

  /**
   * Parse SSE from a complete text response (fallback when streaming isn't available)
   */
  private parseSSEText(
    text: string,
    onStatus?: (status: string) => void,
  ): SendMessageResponse {
    const lines = text.split('\n');
    let finalResponse: SendMessageResponse | null = null;
    let currentEvent = '';

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7);
      } else if (line.startsWith('data: ')) {
        const data = line.slice(6);
        try {
          const parsed = JSON.parse(data);
          if (currentEvent === 'status' && onStatus) {
            onStatus(parsed.status);
          } else if (currentEvent === 'done') {
            finalResponse = parsed;
          } else if (currentEvent === 'error') {
            throw new Error(parsed.message || 'Unknown error');
          }
        } catch (e) {
          if (e instanceof Error && (e.message !== 'Unknown error' && !e.message.includes('Failed'))) {
            // JSON parse error — skip
          } else {
            throw e;
          }
        }
        currentEvent = '';
      }
    }

    if (!finalResponse) {
      throw new Error('No response received from server');
    }

    return finalResponse;
  }

  /**
   * Get conversation history
   */
  async getConversation(conversationId: string): Promise<GetConversationResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ai-assistant');
    
    const response = await this.fetchApi.fetch(
      `${baseUrl}/conversations/${conversationId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get conversation: ${response.status} ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Delete a conversation
   */
  async deleteConversation(conversationId: string): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ai-assistant');
    
    const response = await this.fetchApi.fetch(
      `${baseUrl}/conversations/${conversationId}`,
      {
        method: 'DELETE',
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete conversation: ${response.status} ${errorText}`);
    }
  }

  /**
   * Get user's conversation list
   */
  async getConversations(limit: number = 20): Promise<GetConversationsResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('ai-assistant');

    const response = await this.fetchApi.fetch(
      `${baseUrl}/conversations?limit=${limit}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get conversations: ${response.status} ${errorText}`);
    }

    return await response.json();
  }
}
